import { createFileRoute, Link, notFound, useNavigate } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { useEffect, useMemo } from "react";
import {
  PAGE_SIZE, PRICE_MAX, PRICE_MIN, filterAndSort,
  type CatalogFilters, type SortValue,
} from "@/lib/catalog/products";
import { getCatalogRepository } from "@/lib/catalog/repository";
import { CategoryRegistry } from "@/lib/catalog/registry";
import { CatalogToolbar } from "@/components/catalog/CatalogToolbar";
import { FilterPanel } from "@/components/catalog/FilterPanel";
import { CatalogGrid } from "@/components/catalog/CatalogGrid";
import { EmptyState, ProductGridSkeleton } from "@/components/catalog/CatalogStates";
import { CategoryTabs } from "@/components/catalog/CategoryTabs";
import { FilterChips } from "@/components/catalog/FilterChips";
import { track } from "@/lib/customerio/track";
import { CIO_EVENTS } from "@/lib/customerio/events";
import { canonicalUrl } from "@/lib/seo/site";
import { breadcrumbSchema, clampDescription, itemListSchema, ldScript } from "@/lib/seo/schema";


const searchSchema = z.object({
  q: fallback(z.string(), "").default(""),
  family: fallback(z.string().array(), []).default([]),
  gender: fallback(z.string().array(), []).default([]),
  concentration: fallback(z.string().array(), []).default([]),
  collection: fallback(z.string(), "").default(""),
  min: fallback(z.number(), PRICE_MIN).default(PRICE_MIN),
  max: fallback(z.number(), PRICE_MAX).default(PRICE_MAX),
  sort: fallback(z.string(), "featured").default("featured"),
  page: fallback(z.number(), 1).default(1),
  attrs: fallback(z.record(z.string(), z.string().array()), {}).default({}),
  ranges: fallback(z.record(z.string(), z.object({ min: z.number().optional(), max: z.number().optional() })), {}).default({}),
  bools: fallback(z.record(z.string(), z.boolean()), {}).default({}),
});
type Search = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/shop/$category")({
  validateSearch: zodValidator(searchSchema),
  loader: async ({ params }) => {
    const plugin = CategoryRegistry.getBySlug(params.category);
    if (!plugin) throw notFound();
    const products = await getCatalogRepository().getProductsByCategory(plugin.metadata.id);
    // Only serializable data may cross the SSR boundary — the plugin holds a
    // Zod schema (functions) and would break router dehydration.
    return { categorySlug: plugin.metadata.slug, metadata: { ...plugin.metadata }, products };
  },
  pendingComponent: () => <ProductGridSkeleton />,
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="text-3xl text-black" style={{ fontFamily: "'Playfair Display', serif" }}>Category not found</h1>
      <p className="mt-4 text-sm text-black/60">This category is not part of the CB16 maison.</p>
      <Link to="/shop" className="mt-8 inline-block border border-black px-6 py-3 text-[11px] uppercase tracking-[0.28em] hover:border-[#C6A15B] hover:text-[#C6A15B]">All categories</Link>
    </div>
  ),
  head: ({ loaderData, params }) => {
    const m = loaderData?.metadata;
    const products = loaderData?.products ?? [];
    const title = m ? `${m.name} | CB16 Fragrances` : "Category | CB16 Fragrances";
    const desc = clampDescription(m?.tagline || "Explore CB16 collections.");

    const url = canonicalUrl(`/shop/${params.category}`);
    return {
      meta: [
        { title }, { name: "description", content: desc },
        { property: "og:url", content: url },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        ldScript(
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Shop", path: "/shop" },
            ...(m ? [{ name: m.name, path: `/shop/${params.category}` }] : []),
          ]),
        ),
        ...(products.length ? [ldScript(itemListSchema(products))] : []),
      ],
    };
  },

  component: ShopCategoryPage,
});

function ShopCategoryPage() {
  const { categorySlug, metadata, products } = Route.useLoaderData();
  const plugin = CategoryRegistry.getBySlug(categorySlug)!;
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  useEffect(() => {
    track(CIO_EVENTS.CATEGORY_VIEWED, {
      category_id: metadata.id,
      category_name: metadata.name,
      products: products.length,
    });
  }, [metadata.id, metadata.name, products.length]);

  const filters: CatalogFilters = useMemo(() => ({
    q: search.q,
    families: search.family,
    genders: search.gender,
    concentrations: search.concentration,
    collection: search.collection,
    min: search.min,
    max: search.max,
    sort: (search.sort as SortValue) ?? "featured",
    attrs: search.attrs,
    ranges: search.ranges,
    bools: search.bools,
  }), [search]);

  const results = useMemo(() => filterAndSort(products, filters), [products, filters]);
  const visibleCount = Math.min(results.length, search.page * PAGE_SIZE);
  const visible = results.slice(0, visibleCount);
  const canLoadMore = visibleCount < results.length;

  function applyPatch(patch: Partial<CatalogFilters>) {
    navigate({ search: (prev: Search) => ({
      ...prev,
      q: patch.q ?? prev.q,
      family: patch.families ?? prev.family,
      gender: patch.genders ?? prev.gender,
      concentration: patch.concentrations ?? prev.concentration,
      collection: patch.collection ?? prev.collection,
      min: patch.min ?? prev.min,
      max: patch.max ?? prev.max,
      sort: patch.sort ?? prev.sort,
      attrs: patch.attrs ?? prev.attrs,
      ranges: patch.ranges ?? prev.ranges,
      bools: patch.bools ?? prev.bools,
      page: 1,
    }), replace: true });
  }
  function reset() {
    navigate({ search: { q: "", family: [], gender: [], concentration: [], collection: "", min: PRICE_MIN, max: PRICE_MAX, sort: "featured", page: 1, attrs: {}, ranges: {}, bools: {} }, replace: true });
  }
  function loadMore() {
    navigate({ search: (prev: Search) => ({ ...prev, page: prev.page + 1 }), replace: true });
  }

  return (
    <div className="bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <section className="border-b border-black/10 bg-[#faf7f2]">
        <div className="mx-auto max-w-7xl px-6 py-12 lg:px-10 lg:py-16">
          <span className="text-[11px] font-medium uppercase tracking-[0.32em] text-[#C6A15B]">
            The Maison
          </span>
          <h1 className="mt-4 text-4xl font-normal leading-tight text-black sm:text-5xl lg:text-6xl" style={{ fontFamily: "'Playfair Display', serif" }}>{metadata.name}</h1>
          <div className="mt-6 h-px w-10 bg-[#C6A15B]" />
          <p className="mt-6 max-w-xl text-sm leading-relaxed text-black/60 sm:text-base">{metadata.tagline}</p>
          <p className="mt-4 text-[11px] uppercase tracking-[0.28em] text-black/50">{products.length} {products.length === 1 ? "product" : "products"}</p>
        </div>
      </section>

      <CategoryTabs activeSlug={metadata.slug} />

      <div className="mx-auto max-w-7xl px-6 py-10 lg:px-10">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[260px_minmax(0,1fr)] lg:gap-12">
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <FilterPanel filters={filters} onChange={applyPatch} onReset={reset} showCollection={true} facets={plugin.facets} />
            </div>
          </aside>
          <div className="min-w-0">
            <CatalogToolbar filters={filters} totalCount={results.length} onChange={applyPatch} onReset={reset} />
            <FilterChips filters={filters} facets={plugin.facets} onChange={applyPatch} onReset={reset} />
            <div className="mt-6">
              {results.length === 0 && <EmptyState onReset={reset} />}
              {results.length > 0 && (
                <>
                  <CatalogGrid products={visible} />
                  {canLoadMore && (
                    <div className="mt-16 flex justify-center">
                      <button type="button" onClick={loadMore} className="border border-black px-8 py-3 text-[11px] font-medium uppercase tracking-[0.28em] text-black transition hover:border-[#C6A15B] hover:text-[#C6A15B]">
                        Load More ({results.length - visibleCount} remaining)
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}